import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { User } from "../auth/auth";
import { camelToSnake, snakeToCamel } from "caseparser";

export const branchApi = api
  .enhanceEndpoints({ addTagTypes: ["personal", "specialists", "branchs"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      branchs: builder.query({
        query: () => ({
          url: `branchs`,
          method: RequestMethod.GET,
        }),
        providesTags: ["branchs"],
        transformResponse: (response: any) => response?.data,
      }),
      branchsGym: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/branchs-gym`,
          method: RequestMethod.GET,
        }),
        providesTags: ["branchs"],
        transformResponse: (response: any) => response?.data,
      }),
      getBranch: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/get-branch`,
          method: RequestMethod.GET,
        }),
        providesTags: ["branchs"],
        transformResponse: (response: any) => response?.data,
      }),
      createBranch: builder.mutation({
        query: (branchData) => ({
          url: "/branchs",
          method: RequestMethod.POST,
          body: branchData,
        }),
      }),
      updateBranch: builder.mutation({
        query: ({ id, branchData }) => ({
          url: `branchs/${id}`,
          method: RequestMethod.POST,
          body: branchData,
        }),
        invalidatesTags: ["branchs"],
        transformResponse: (response: any) => response?.data,
      }),
      deleteBranch: builder.mutation({
        query: ({ id }) => ({
          url: `branchs/${id}`,
          method: RequestMethod.DELETE,
        }),
        invalidatesTags: ["branchs"],
        transformResponse: (response: any) => response?.data,
      }),
      specialists: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/specialists`,
          method: RequestMethod.GET,
        }),
        providesTags: ["specialists"],
        transformResponse: (response: any) => response?.data,
      }),
      personal: builder.query({
        providesTags: ["personal"],
        query: ({ branchId, search, page }) => {
          return {
            url: `branchs/${branchId}/personal`,
            method: RequestMethod.GET,
            params: camelToSnake({
              page,
              search,
            }),
          };
        },
        transformResponse: (response: any): any => ({
          data: snakeToCamel(response?.data),
          perPage: response?.per_page,
          total: response?.total,
          lastPage: response?.last_page,
        }),
      }),
      permissionsBranch: builder.mutation({
        query: (branchData) => ({
          url: "roles/clone/1",
          method: RequestMethod.POST,
          body: branchData,
        }),
      }),
      createPersonal: builder.mutation({
        query: (gymData) => ({
          url: "users",
          method: RequestMethod.POST,
          body: gymData,
        }),
      }),
      updatePersonal: builder.mutation({
        query: ({ id, personData }: any) => ({
          url: `users/${id}`,
          method: RequestMethod.POST,
          body: personData,
        }),
        invalidatesTags: ["personal"],
      }),
      deletePersonal: builder.mutation({
        query: ({ id }) => ({
          url: `users/${id}`,
          method: RequestMethod.DELETE,
        }),
        invalidatesTags: ["personal"],
      }),
      deleteImage: builder.mutation({
        query: ({ id }: any) => ({
          url: `users/${id}/image`,
          method: RequestMethod.DELETE,
        }),
        invalidatesTags: ["personal"],
      }),
      branches: builder.query({
        query: () => ({
          url: "/branches",
          method: RequestMethod.GET,
        }),
      }),
      fetchBranchs: builder.mutation({
        query: (searchTerm) => ({
          url: "/admin/branchs/search",
          method: RequestMethod.POST,
          body: searchTerm,
        }),
      }),
      fetchGymsForBranch: builder.mutation({
        query: () => ({
          url: `/admin/branchs/gyms`,
          method: RequestMethod.GET,
        }),
      }),
      updateHours: builder.mutation({
        query: (params) => ({
          url: `branchs/${params.branchId}/hours`,
          method: RequestMethod.PUT,
          body: camelToSnake(params),
        }),
      }),
      updateBranchNew: builder.mutation({
        query: ({ branchId, body }) => ({
          url: `/branchs/${branchId}`,
          method: RequestMethod.POST,
          body,
        }),
      }),
      getPersonalRoles: builder.query({
        query: ({ branchId }) => {
          return {
            url: `branchs/${branchId}/personal_roles`,
            method: RequestMethod.GET,
          };
        },
      }),
    }),
  });

export const {
  useBranchsQuery,
  useBranchsGymQuery,
  usePersonalQuery,
  useBranchesQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useUpdatePersonalMutation,
  useDeletePersonalMutation,
  useDeleteBranchMutation,
  useFetchBranchsMutation,
  useFetchGymsForBranchMutation,
  useSpecialistsQuery,
  useCreatePersonalMutation,
  useDeleteImageMutation,
  useGetBranchQuery,
  useUpdateHoursMutation,
  useUpdateBranchNewMutation,
  useGetPersonalRolesQuery,
} = branchApi;
